/**
 * <pdt-lausanne-opening>
 *   Ribbon + popup for the Lausanne boutique opening (sections/pdt-lausanne-opening.liquid).
 *   - Picks the "soon" / "open" texts from today's date in Zurich, so a page served
 *     from cache still switches on the opening day, and hides everything after the end date.
 *   - Opens the popup once per visitor (localStorage) or once per visit (session cookie),
 *     counted per state, so it shows once more from the opening day.
 *   - In the theme editor the popup only opens while its section is selected.
 */
(function () {
  if (customElements.get('pdt-lausanne-opening')) return;

  const STORAGE_PREFIX = 'pdt-lausanne-opening-';
  const MAX_WAIT_ATTEMPTS = 15;
  let selectedSectionId = null;

  function zurichToday() {
    try {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Zurich',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).formatToParts(new Date());
      const part = (type) => parts.find((p) => p.type === type).value;
      return Number(part('year') + part('month') + part('day'));
    } catch (e) {
      const d = new Date();
      return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
    }
  }

  function parseDate(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || '').trim());
    return match ? Number(match[1] + match[2] + match[3]) : null;
  }

  /** Where the "already seen" flag lives. A session cookie is shared by all tabs and cleared when the browser closes. */
  const flagStores = {
    visitor: {
      read: (key) => window.localStorage.getItem(key) === '1',
      write: (key) => window.localStorage.setItem(key, '1'),
    },
    visit: {
      read: (key) => document.cookie.split('; ').includes(`${key}=1`),
      write: (key) => {
        document.cookie = `${key}=1; path=/; SameSite=Lax`;
      },
    },
  };

  /** 'visit' = once per visit; anything else = once per visitor, with the cookie as fallback when localStorage is blocked. */
  function storesFor(frequency) {
    return frequency === 'visit' ? [flagStores.visit] : [flagStores.visitor, flagStores.visit];
  }

  function readFlag(key, stores) {
    for (const store of stores) {
      try {
        return store.read(key);
      } catch (e) {
        /* storage blocked: try the next one */
      }
    }
    return false;
  }

  function writeFlag(key, stores) {
    for (const store of stores) {
      try {
        store.write(key);
        return;
      } catch (e) {
        /* storage blocked: try the next one */
      }
    }
  }

  /** Another overlay is up (cart drawer, quick add, mobile menu, cookies info): wait for it. */
  function pageIsBusy(ownDialog) {
    const openDialog = Array.from(document.querySelectorAll('dialog[open]')).some((d) => d !== ownDialog);
    const mobileMenuOpen = document.body.dataset.mobileMenu === 'open';
    const cookiesModal = document.getElementById('cookies-modal');
    return openDialog || mobileMenuOpen || Boolean(cookiesModal && !cookiesModal.hidden);
  }

  class PdtLausanneOpening extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-pdt-lo-popup]');
      this.designMode = Boolean(window.Shopify && window.Shopify.designMode);
      this.flagStores = storesFor(this.dataset.popupFrequency);

      const today = zurichToday();
      const opening = parseDate(this.dataset.openingDate);
      const end = parseDate(this.dataset.endDate);

      this.state = opening && today >= opening ? 'open' : 'soon';
      this.dataset.state = this.state;

      if (end && today > end && !this.designMode) {
        this.hidden = true;
        return;
      }

      if (!this.dialog) return;

      this.dialog.addEventListener('click', this.#onDialogClick);
      // Escape fires `cancel`; Chromium only dispatches `close` with the next animation frame.
      this.dialog.addEventListener('cancel', this.#afterClose);
      this.dialog.addEventListener('close', this.#afterClose);
      this.querySelectorAll('[data-pdt-lo-close]').forEach((button) => button.addEventListener('click', this.#close));
      this.querySelector('[data-pdt-lo-cta]')?.addEventListener('click', this.#remember);
      this.querySelector('[data-pdt-lo-copy]')?.addEventListener('click', this.#copyCode);

      if (this.designMode) {
        if (selectedSectionId === this.dataset.sectionId) this.#open();
        return;
      }

      // Back from the newsletter form (success or error): show the result straight away.
      if (this.dataset.newsletter) {
        this.#open();
        return;
      }

      if (readFlag(this.#storageKey, this.flagStores)) return;

      const delay = Math.max(0, parseInt(this.dataset.popupDelay || '0', 10) || 0) * 1000;
      this.#timer = setTimeout(() => this.#openWhenFree(0), delay);
    }

    disconnectedCallback() {
      clearTimeout(this.#timer);
      document.documentElement.classList.remove('pdt-lo-lock');
    }

    openInEditor() {
      if (this.dialog) this.#open();
    }

    closeInEditor() {
      if (this.dialog) this.#close();
    }

    get #storageKey() {
      return STORAGE_PREFIX + this.state;
    }

    #openWhenFree = (attempt) => {
      if (!this.isConnected || this.hidden) return;
      if (pageIsBusy(this.dialog) && attempt < MAX_WAIT_ATTEMPTS) {
        this.#timer = setTimeout(() => this.#openWhenFree(attempt + 1), 2000);
        return;
      }
      if (!pageIsBusy(this.dialog)) this.#open();
    };

    #open() {
      if (this.dialog.open) return;
      this.dialog.showModal();
      document.documentElement.classList.add('pdt-lo-lock');
    }

    #close = () => {
      if (this.dialog.open) this.dialog.close();
      this.#afterClose();
    };

    #afterClose = () => {
      document.documentElement.classList.remove('pdt-lo-lock');
      this.#remember();
    };

    #remember = () => {
      if (!this.designMode) writeFlag(this.#storageKey, this.flagStores);
    };

    #copyCode = async (event) => {
      const button = event.currentTarget;
      const codeElement = this.querySelector('[data-pdt-lo-code]');
      if (!codeElement) return;

      try {
        await navigator.clipboard.writeText(codeElement.textContent.trim());
      } catch (e) {
        // No clipboard access: select the code so it can be copied by hand.
        const range = document.createRange();
        range.selectNodeContents(codeElement);
        window.getSelection().removeAllRanges();
        window.getSelection().addRange(range);
        return;
      }

      const label = button.textContent;
      button.textContent = button.dataset.copiedLabel || label;
      setTimeout(() => {
        button.textContent = label;
      }, 2000);
    };

    /** Clicks on the backdrop land on the <dialog> itself; clicks inside land on the panel. */
    #onDialogClick = (event) => {
      if (event.target === this.dialog) this.#close();
    };

    #timer = undefined;
  }

  customElements.define('pdt-lausanne-opening', PdtLausanneOpening);

  document.addEventListener('shopify:section:select', (event) => {
    selectedSectionId = event.detail.sectionId;
    document
      .querySelectorAll(`pdt-lausanne-opening[data-section-id="${CSS.escape(selectedSectionId)}"]`)
      .forEach((element) => element.openInEditor());
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    if (selectedSectionId === event.detail.sectionId) selectedSectionId = null;
    document
      .querySelectorAll(`pdt-lausanne-opening[data-section-id="${CSS.escape(event.detail.sectionId)}"]`)
      .forEach((element) => element.closeInEditor());
  });
})();
